import { AnalyticsAdapter, AnalyticsOccurrence } from 'components/shared/analytics/analytics-types.js'
import { productPrice, toGa4Item } from 'components/shared/analytics/adapters/ga4/ga4-item-mapper.js'

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

const GTM_SCRIPT_SELECTOR = 'script[src*="googletagmanager.com/gtm.js"]'

export function isGtmPresent(): boolean {
  if (typeof window === 'undefined') return false
  if (Array.isArray(window.dataLayer)) return true
  return typeof document !== 'undefined' && document.querySelector(GTM_SCRIPT_SELECTOR) !== null
}

function assertNever(occurrence: never): never {
  throw new Error(`ga4-adapter: unhandled analytics occurrence '${JSON.stringify(occurrence)}'`)
}

function toGa4Event(occurrence: AnalyticsOccurrence): Record<string, unknown> {
  switch (occurrence.type) {
    case 'product-selected':
      return {
        event: 'select_item',
        ecommerce: {
          items: [toGa4Item(occurrence.product, occurrence.context)],
        },
      }

    case 'product-added-to-basket':
      return {
        event: 'add_to_cart',
        ecommerce: {
          currency: occurrence.product.prices.currencyCode,
          value: productPrice(occurrence.product) * occurrence.quantity,
          items: [toGa4Item(occurrence.product, occurrence.context, occurrence.quantity)],
        },
      }

    case 'product-added-to-wishlist':
      return {
        event: 'add_to_wishlist',
        ecommerce: {
          currency: occurrence.product.prices.currencyCode,
          value: productPrice(occurrence.product),
          items: [toGa4Item(occurrence.product, occurrence.context)],
        },
      }

    default:
      return assertNever(occurrence)
  }
}

function pushGa4Event(event: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push(event)
}

let hasReportedAbsence = false

export function resetGa4AdapterAbsenceReport(): void {
  hasReportedAbsence = false
}

export function createGa4Adapter(): AnalyticsAdapter | null {
  if (!isGtmPresent()) {
    if (!hasReportedAbsence) {
      hasReportedAbsence = true
      console.warn('[ga4-adapter] GTM was not detected on the page - the GA4 analytics adapter did not register')
    }
    return null
  }

  return {
    name: 'ga4',
    handle: (occurrence: AnalyticsOccurrence): void => {
      pushGa4Event(toGa4Event(occurrence))
    },
  }
}
